import { useMemo, useState } from 'react'

import { Schema } from 'yup'

import { ISurveyOption, ISurveyQuestionData, ISurveyState } from 'components/onboardingWelcome'

type UseSurveyDataProps = {
  questions: Array<ISurveyQuestionData>
}

export const useSurvey = ({ questions }: UseSurveyDataProps): ISurveyState => {
  const [currentStep, setCurrentStep] = useState<number>(0)

  const currentQuestion: ISurveyQuestionData = useMemo(() => {
    const question = questions[currentStep]
    return { ...question }
  }, [currentStep, questions])

  const isLastPage = currentStep === Math.max(questions.length - 1, 0)

  const steps = questions.length

  const handlePrevious = () => {
    setCurrentStep(prevState => Math.max(prevState - 1, 0))
  }

  const handleNext = () => {
    setCurrentStep(prevState => Math.min(prevState + 1))
  }

  // Validate only the current step fields
  const validate = async (step: number, values: object, schema: Array<Schema>) => {
    try {
      await schema[step]?.validate(values, { abortEarly: false })
    } catch (e: any) {
      return e.inner.reduce((acc, error) => {
        return {
          ...acc,
          [error.path]: error.message
        }
      }, {})
    }
  }

  const generateShuffledOptions = (
    options: Array<ISurveyOption>
  ): Array<{
    text: string
    description: string | null
    value: string
  }> => {
    const optionsData = options.map(({ id, label, subtitle }) => ({
      description: subtitle,
      id: id,
      text: label,
      value: id
    }))

    const otherOption = optionsData.filter(({ value }) => value === 'other')

    if (otherOption.length) {
      const nonOtherOptions = optionsData.filter(({ value }) => value !== 'other')
      // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
      return [...nonOtherOptions, ...otherOption]
    }

    // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
    return optionsData
  }

  // Reorganize survey question data for simplified dropdown versions of survey
  const generateDropdownSurvey = (
    questionData: Array<ISurveyQuestionData>,
    defaultOption: { text: string; value: string; disabled: boolean; id: string }
  ) =>
    questionData.flatMap(({ component, datas }) => {
      if (component === 'Question') {
        return datas.map(({ id, options, title }) => ({
          options: [
            // Default option, displays as default label for dropdown
            defaultOption,
            // Randomise order of the options except "other" option which is always last
            ...generateShuffledOptions(options)
          ],
          questionId: id,
          title: title
        }))
      }
      return []
    })

  return {
    currentQuestion,
    currentStep,
    // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
    generateDropdownSurvey,
    handleNext,
    handlePrevious,
    isLastPage,
    steps,
    validate
  }
}
